import React, { useEffect, useRef, useState } from 'react';
import { sb } from '../supabase';
import PhoneField, { parseStoredPhone } from './PhoneField';
import { saveProfilePhoneIfBlank } from '../lib/profilePhone';
import { myPhoneStatus, phoneLinkErrorKey, startPhoneLink, verifyPhoneLink } from '../lib/phoneLink';

// The trader's phone, VERIFIED by a WhatsApp code (owner, 2026-10-01).
//
// Used on the two request kinds our team works by phone: a managed request
// (RequestWizard) and an idea request with idea_mode 'team_search'
// (IdeaToProduct). The server is the authority — a BEFORE INSERT trigger on
// requests (supabase/migrations/20261001122153_request_phone_verified.sql, applied 2026-10-01) refuses those rows
// unless contact_phone is the buyer's auth.users.phone with phone_confirmed_at
// set. This field exists so the trader meets that rule here, not as an error.
//
// CHANGED 2026-10-21 (migration phone_ownership): "verified" is the server's
// phone_is_owned(), read through my_phone_status() — NOT phone_confirmed_at.
// With phone confirmations off, GoTrue's updateUser({ phone }) marked a number
// confirmed WITHOUT sending any code, so the old path here verified nothing.
//   · the account's number is already proven → prefilled, ✓, no code;
//   · otherwise, or the trader edits the number → the phone-link function
//     sends our own WhatsApp code and, once it is typed, sets the account's
//     phone and records it as proven (src/lib/phoneLink.js).
// ⚠️ For an account that SIGNS IN by phone, a changed number is its sign-in
// number from then on — the note under the field says so.
//
// Props:
//   value          — a number to prefill when the account has no verified phone
//                    (profiles.phone, a restored draft). Read until the trader types.
//   onVerified(e164) — the verified E.164, or '' whenever the number shown is not
//                    verified. The parent's submit is disabled on ''.
//   onNumberChange(e164) — optional; every edit, verified or not (draft keeping).
//   lang, profile, setProfile — profile copy of the number (see verify()).
//   refusal        — { hint, n } from the parent when the insert was refused with
//                    hint phone_required / phone_not_verified; bump n to re-show.
//   inputClassName / selectClassName / inputStyle / selectStyle → PhoneField.

const COOLDOWN_S = 60;
// phone-link issues exactly 6 digits (its own code, not GoTrue's).
const CODE_MIN = 6;
const CODE_MAX = 6;

const T = {
  ar: {
    verified: 'موثّق عبر واتساب',
    send: 'أرسل رمز التحقق عبر واتساب',
    resend: 'أعد إرسال الرمز',
    resendIn: (s) => `إعادة الإرسال بعد ${s} ث`,
    sent: (n) => `أرسلنا رمز التحقق إلى واتساب ${n}.`,
    codeLabel: 'رمز التحقق',
    codePh: 'أدخل الرمز',
    verify: 'تحقّق',
    verifying: 'جارٍ التحقق…',
    sending: 'جارٍ الإرسال…',
    needVerify: 'وثّق رقمك عبر واتساب لتتمكن من إرسال الطلب.',
    becomesAccount: 'بعد التحقق يصبح هذا الرقم رقم حسابك الموثّق.',
    invalid: 'يرجى إدخال رقم جوال صحيح.',
    badCode: 'الرمز غير صحيح أو انتهت صلاحيته.',
    taken: 'هذا الرقم مرتبط بحساب آخر في معبر.',
    rate: 'طلبات كثيرة خلال وقت قصير. انتظر قليلاً ثم أعد المحاولة.',
    sendFailed: 'تعذّر إرسال الرمز. حاول مرة أخرى.',
    signIn: 'سجّل الدخول لتوثيق رقمك عبر واتساب.',
    phone_required: 'رقم جوال موثّق مطلوب لهذا الطلب.',
    phone_not_verified: 'رقم الجوال في هذا الطلب غير موثّق. وثّقه عبر واتساب ثم أرسل الطلب.',
  },
  en: {
    verified: 'Verified via WhatsApp',
    send: 'Send code via WhatsApp',
    resend: 'Resend code',
    resendIn: (s) => `Resend in ${s}s`,
    sent: (n) => `We sent a verification code to WhatsApp ${n}.`,
    codeLabel: 'Verification code',
    codePh: 'Enter the code',
    verify: 'Verify',
    verifying: 'Verifying…',
    sending: 'Sending…',
    needVerify: 'Verify your number via WhatsApp to send this request.',
    becomesAccount: "Once verified, this becomes your account's verified number.",
    invalid: 'Please enter a valid mobile number.',
    badCode: 'The code is wrong or has expired.',
    taken: 'This number is linked to another Maabar account.',
    rate: 'Too many attempts in a short time. Please wait a moment and try again.',
    sendFailed: "We couldn't send the code. Please try again.",
    signIn: 'Sign in to verify your number via WhatsApp.',
    phone_required: 'A verified phone number is required for this request.',
    phone_not_verified: 'The phone number on this request is not verified. Verify it via WhatsApp, then send the request.',
  },
  zh: {
    verified: '已通过 WhatsApp 验证',
    send: '通过 WhatsApp 发送验证码',
    resend: '重新发送验证码',
    resendIn: (s) => `${s} 秒后可重新发送`,
    sent: (n) => `验证码已发送至 WhatsApp ${n}。`,
    codeLabel: '验证码',
    codePh: '输入验证码',
    verify: '验证',
    verifying: '验证中…',
    sending: '发送中…',
    needVerify: '请先通过 WhatsApp 验证您的号码，再提交需求。',
    becomesAccount: '验证后，此号码将成为您账户的已验证号码。',
    invalid: '请输入有效的手机号码。',
    badCode: '验证码错误或已过期。',
    taken: '此号码已绑定另一个 Maabar 账户。',
    rate: '尝试次数过多，请稍后再试。',
    sendFailed: '验证码发送失败，请重试。',
    signIn: '请登录后通过 WhatsApp 验证您的号码。',
    phone_required: '此需求需要已验证的手机号码。',
    phone_not_verified: '此需求中的手机号码未经验证。请通过 WhatsApp 验证后再提交。',
  },
};

// The number inside an Arabic sentence, isolated so it is not reordered.
const isolate = (n) => `\u2068${n}\u2069`;

const digitsOf = (v) => String(v || '').replace(/[^0-9]/g, '');

// The server's refusal of the insert → 'phone_required' | 'phone_not_verified' | null.
// Read from the PostgREST error's `hint` (set by enforce_verified_request_phone),
// with the message as a fallback in case a proxy drops the hint.
export function requestPhoneRefusal(error) {
  if (!error) return null;
  const hint = String(error.hint || '').trim();
  if (hint === 'phone_required' || hint === 'phone_not_verified') return hint;
  const m = String(error.message || '').toLowerCase();
  if (m.includes('verified phone number is required')) return 'phone_required';
  if (m.includes('phone number on this request is not verified')) return 'phone_not_verified';
  return null;
}

// The text for a refusal hint, for the parent's own error line by the submit.
export function phoneRefusalMessage(hint, lang = 'ar') {
  const t = T[lang] || T.ar;
  return t[hint] || t.phone_not_verified;
}

export default function VerifiedPhoneField({
  value = '',
  onVerified,
  onNumberChange,
  lang = 'ar',
  profile,
  setProfile,
  refusal,
  inputClassName,
  selectClassName,
  inputStyle,
  selectStyle,
}) {
  const t = T[lang] || T.ar;
  const isAr = lang === 'ar';
  const font = { fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' };
  const rootRef = useRef(null);

  // The account as GoTrue has it: '+<digits>' and whether it is confirmed.
  const [auth, setAuth] = useState({ loaded: false, signedIn: false, phone: '', confirmed: false });
  const [number, setNumber] = useState(() => parseStoredPhone(value));
  const [fieldKey, setFieldKey] = useState(0);
  const touched = useRef(false);
  const [verified, setVerified] = useState('');          // the E.164 that is verified
  const [codeFor, setCodeFor] = useState('');            // the E.164 a code was sent to
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState('');                  // '' | 'send' | 'verify'
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');

  const isVerified = Boolean(verified) && verified === number.e164;

  // Re-read the account (mount, and after a server refusal — an old session
  // may be what the refusal was about). A confirmed auth phone wins over any
  // prefill until the trader edits the field.
  const loadAuth = async ({ force = false } = {}) => {
    const { data, error } = await sb.auth.getUser();
    if (error) console.error('[VerifiedPhoneField] getUser failed:', error.message || error);
    const user = data?.user || null;
    const d = digitsOf(user?.phone);
    const phone = d ? `+${d}` : '';
    // Proven by a code (server), not merely "confirmed" by GoTrue.
    const confirmed = Boolean(phone && user && (await myPhoneStatus()).owned);
    setAuth({ loaded: true, signedIn: Boolean(user), phone, confirmed });
    if (confirmed && (force || !touched.current)) {
      touched.current = false;
      setNumber({ e164: phone, valid: true });
      setVerified(phone);
      setCodeFor('');
      setFieldKey((k) => k + 1);
    }
  };
  useEffect(() => { loadAuth(); /* once */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A prefill that arrives later (the profile loads after mount). PhoneField
  // reads its value once, so it is remounted — unless the trader has typed or
  // the account already supplied a verified number.
  useEffect(() => {
    if (touched.current || auth.confirmed || number.e164 || !value) return;
    setNumber(parseStoredPhone(value));
    setFieldKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, auth.confirmed]);

  // The parent only ever holds a VERIFIED number, or ''.
  useEffect(() => {
    onVerified?.(isVerified ? number.e164 : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerified, number.e164]);

  // The insert was refused by the server: say which rule, drop back to the
  // phone step, and re-read the account.
  useEffect(() => {
    if (!refusal?.hint) return;
    setErr(t[refusal.hint] || t.phone_not_verified);
    setInfo('');
    setVerified('');
    setCodeFor('');
    setCode('');
    loadAuth({ force: true });
    try { rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refusal?.n]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const onChange = (e164, valid) => {
    touched.current = true;
    setNumber({ e164, valid });
    onNumberChange?.(e164);
    setErr('');
    // Typed back to the account's verified number → verified again, no code.
    if (auth.confirmed && e164 && digitsOf(e164) === digitsOf(auth.phone)) setVerified(e164);
    if (codeFor && e164 !== codeFor) { setCodeFor(''); setCode(''); setInfo(''); }
  };

  const sendCode = async () => {
    if (busy || cooldown > 0) return;
    if (!number.valid) { setErr(t.invalid); return; }
    const e164 = number.e164;
    if (auth.confirmed && digitsOf(e164) === digitsOf(auth.phone)) { setVerified(e164); return; }
    setBusy('send'); setErr(''); setInfo('');
    // One path for every case (no number, an unproven one, a new one): our
    // own code via phone-link. The account's number changes only once the
    // code is typed.
    const r = await startPhoneLink(e164, lang);
    setBusy('');
    if (!r.ok) {
      console.error('[VerifiedPhoneField] send code failed:', r.httpStatus || '', r.error || '');
      setErr(t[phoneLinkErrorKey(r.error)]);
      return;
    }
    setCodeFor(e164);
    setCode('');
    setCooldown(COOLDOWN_S);
    setInfo(t.sent(isolate(e164)));
  };

  const verify = async () => {
    const token = code.replace(/\D/g, '');
    if (busy || !codeFor) return;
    if (token.length < CODE_MIN) { setErr(t.badCode); return; }
    setBusy('verify'); setErr('');
    const r = await verifyPhoneLink(token);
    setBusy('');
    if (!r.ok) {
      console.error('[VerifiedPhoneField] verify failed:', r.httpStatus || '', r.error || '');
      setErr(t[phoneLinkErrorKey(r.error)]);
      return;
    }
    const e164 = codeFor;
    setAuth((a) => ({ ...a, phone: e164, confirmed: true }));
    setVerified(e164);
    setCodeFor(''); setCode(''); setInfo('');
    // The profile copy (owner, 2026-10-01). auth.users.phone is what the server
    // checks, so a failure here is logged (inside the helper) and never blocks
    // the request. Blank profiles only — the helper never overwrites a number
    // the trader saved in Settings.
    const { data: sess } = await sb.auth.getSession();
    const userId = sess?.session?.user?.id;
    if (userId) {
      saveProfilePhoneIfBlank({ userId, profile, e164 })
        .then((saved) => { if (saved) setProfile?.((p) => (p ? { ...p, phone: e164 } : p)); });
    }
  };

  const btn = (enabled) => ({
    ...font, border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 700,
    background: enabled ? 'var(--ink, #1a1814)' : 'var(--bg-subtle)', color: enabled ? 'var(--on-dark, #fff)' : 'var(--text-muted)',
    cursor: enabled ? 'pointer' : 'default',
  });

  const showSend = auth.signedIn && !isVerified && !codeFor;
  const changingAccountNumber = auth.confirmed && number.e164 && !isVerified;

  return (
    <div ref={rootRef} data-clarity-mask="True">
      <PhoneField
        key={fieldKey}
        value={number.e164}
        onChange={onChange}
        defaultCountry="SA"
        lang={lang}
        invalid={Boolean(number.e164) && !number.valid}
        inputClassName={inputClassName}
        selectClassName={selectClassName}
        inputStyle={inputStyle}
        selectStyle={selectStyle}
      />

      {isVerified && (
        <div style={{ ...font, display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12.5, fontWeight: 700, color: '#2e7d32' }}>
          <span aria-hidden>✓</span>{t.verified}
        </div>
      )}

      {auth.loaded && !auth.signedIn && (
        <p style={{ ...font, fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0' }}>{t.signIn}</p>
      )}

      {showSend && (
        <div style={{ marginTop: 10 }}>
          {changingAccountNumber && (
            <p style={{ ...font, fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px', lineHeight: 1.7 }}>{t.becomesAccount}</p>
          )}
          <button type="button" onClick={sendCode} disabled={Boolean(busy) || cooldown > 0 || !number.valid}
            style={btn(!busy && cooldown <= 0 && number.valid)}>
            {busy === 'send' ? t.sending : cooldown > 0 ? t.resendIn(cooldown) : t.send}
          </button>
          <p style={{ ...font, fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0' }}>{t.needVerify}</p>
        </div>
      )}

      {auth.signedIn && !isVerified && codeFor && (
        <div style={{ marginTop: 10 }}>
          {info && <p style={{ ...font, fontSize: 12.5, color: 'var(--text-secondary)', margin: '0 0 8px' }}>{info}</p>}
          <div style={{ display: 'flex', gap: 8, flexDirection: isAr ? 'row-reverse' : 'row' }}>
            <input
              value={code}
              onChange={(ev) => { setCode(ev.target.value.replace(/\D/g, '').slice(0, CODE_MAX)); setErr(''); }}
              onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.preventDefault(); verify(); } }}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={CODE_MAX}
              placeholder={t.codePh}
              aria-label={t.codeLabel}
              dir="ltr"
              className={inputClassName}
              style={{ flex: 1, minWidth: 0, textAlign: 'center', letterSpacing: '0.3em', fontSize: 18, ...(inputStyle || {}) }}
            />
            <button type="button" onClick={verify} disabled={Boolean(busy) || code.length < CODE_MIN}
              style={btn(!busy && code.length >= CODE_MIN)}>
              {busy === 'verify' ? t.verifying : t.verify}
            </button>
          </div>
          <button type="button" onClick={sendCode} disabled={Boolean(busy) || cooldown > 0}
            style={{ ...font, background: 'none', border: 'none', padding: '8px 0 0', fontSize: 12, textDecoration: cooldown > 0 ? 'none' : 'underline', color: 'var(--text-secondary)', cursor: cooldown > 0 ? 'default' : 'pointer' }}>
            {cooldown > 0 ? t.resendIn(cooldown) : t.resend}
          </button>
        </div>
      )}

      {err && (
        <p role="alert" style={{ ...font, fontSize: 12.5, color: '#c0392b', margin: '8px 0 0', lineHeight: 1.7 }}>{err}</p>
      )}
    </div>
  );
}
